import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trophy, Target, Flame, Zap, Award, Medal, Star, TrendingUp, ArrowLeft, ChevronRight, Crown, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';

export default function PlayerStatsPage({ user, setUser }) {
  const [searchParams] = useSearchParams();
  const [leaderboards, setLeaderboards] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(searchParams.get('league') || 'all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('batting');

  useEffect(() => {
    fetchLeagues();
  }, []);

  useEffect(() => {
    fetchLeaderboards();
  }, [selectedLeague]);

  const fetchLeagues = async () => {
    try {
      const response = await axios.get(`${API}/leagues`);
      setLeagues(response.data);
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      const url = selectedLeague && selectedLeague !== 'all' 
        ? `${API}/leagues/${selectedLeague}/leaderboards`
        : `${API}/stats/leaderboards`;
      const response = await axios.get(url);
      setLeaderboards(response.data);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const LeaderboardCard = ({ title, icon: Icon, color, data, type, badge }) => {
    const getColumns = () => {
      if (type === 'batting') {
        return ['Runs', 'Balls', 'SR', 'Avg', '4s', '6s'];
      } else if (type === 'bowling') {
        return ['Wkts', 'Runs', 'Overs', 'Econ', 'Avg', 'Best'];
      } else if (type === 'strike_rate') {
        return ['SR', 'Runs', 'Balls', '4s', '6s'];
      } else if (type === 'economy') {
        return ['Econ', 'Wkts', 'Overs', 'Runs', 'Avg'];
      } else if (type === 'sixes' || type === 'fours') {
        return [type === 'sixes' ? '6s' : '4s', 'Runs', 'Balls', 'SR'];
      } else if (type === 'fifties') {
        return ['100s', '50s', 'Runs', 'Inn'];
      } else if (type === 'catches') {
        return ['Catches', 'Run Outs', 'Stumpings', 'Total'];
      }
      return [];
    };

    const getValue = (player, col) => {
      const map = {
        'Runs': player.runs,
        'Balls': player.balls,
        'SR': player.strike_rate,
        'Avg': player.average,
        '4s': player.fours,
        '6s': player.sixes,
        'Wkts': player.wickets,
        'Overs': player.overs,
        'Econ': player.economy,
        'Best': player.best,
        '100s': player.hundreds,
        '50s': player.fifties,
        'Inn': player.innings,
        'Catches': player.catches,
        'Run Outs': player.run_outs,
        'Stumpings': player.stumpings,
        'Total': player.total
      };
      return map[col] ?? '-';
    };

    const columns = getColumns();

    return (
      <Card className="overflow-hidden border-0 shadow-lg">
        <CardHeader className={`${color} text-white py-4`}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <span className="text-lg font-bold">{title}</span>
                {badge && (
                  <Badge className="ml-2 bg-white/20 hover:bg-white/30 text-white text-xs">
                    {badge}
                  </Badge>
                )}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data && data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="py-3 px-4 text-left">#</th>
                    <th className="py-3 px-4 text-left">Player</th>
                    {columns.map(col => (
                      <th key={col} className="py-3 px-2 text-center">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 10).map((player, idx) => (
                    <tr 
                      key={player.player_id} 
                      className={`border-b hover:bg-gray-50 transition-colors ${idx === 0 ? 'bg-teal-50' : idx === 1 ? 'bg-gray-100' : idx === 2 ? 'bg-[#C5D5D5]/30' : ''}`}
                    >
                      <td className="py-3 px-4">
                        {idx === 0 ? (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow">
                            1
                          </div>
                        ) : idx === 1 ? (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white font-bold text-sm shadow">
                            2
                          </div>
                        ) : idx === 2 ? (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#8B1E3F] to-[#6D1830] flex items-center justify-center text-white font-bold text-sm shadow">
                            3
                          </div>
                        ) : (
                          <span className="text-gray-500 font-medium ml-2">{idx + 1}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Link to={`/players/${player.player_id}`} className="hover:text-blue-600">
                          <div className="font-semibold text-gray-800">{player.name}</div>
                          <div className="text-xs text-gray-500">{player.team_short_name || player.team_name}</div>
                        </Link>
                      </td>
                      {columns.map(col => (
                        <td key={col} className={`py-3 px-2 text-center ${col === columns[0] ? 'font-bold text-blue-600' : 'text-gray-600'}`}>
                          {getValue(player, col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No data available
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const TopPerformerCard = ({ title, data, statKey, statLabel, icon: Icon, color }) => {
    if (!data || data.length === 0) return null;
    const top = data[0];

    return (
      <div className={`${color} rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon className="w-6 h-6" />
            <span className="font-semibold">{title}</span>
          </div>
          <Crown className="w-8 h-8 text-yellow-300" />
        </div>
        <Link to={`/players/${top.player_id}`} className="block">
          <div className="text-center py-4">
            <div className="text-5xl font-black mb-2">{top[statKey]}</div>
            <div className="text-white/80 text-sm">{statLabel}</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">{top.name}</div>
            <div className="text-white/70 text-sm">{top.team_name}</div>
          </div>
        </Link>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading stats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="text-white/80 hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </Link>
            <Select value={selectedLeague} onValueChange={setSelectedLeague}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white" data-testid="league-filter">
                <SelectValue placeholder="All Leagues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leagues</SelectItem>
                {leagues.map(league => (
                  <SelectItem key={league.league_id} value={league.league_id}>
                    {league.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Activity className="w-8 h-8" />
              <h1 className="text-3xl md:text-4xl font-black">Player Statistics</h1>
            </div>
            <p className="text-white/80">
              {selectedLeague !== 'all' ? leaderboards?.league?.name : 'All Leagues'} - Leaderboards & Rankings
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Top Performers Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <TopPerformerCard 
            title="Top Scorer" 
            data={leaderboards?.most_runs} 
            statKey="runs" 
            statLabel="RUNS"
            icon={Trophy}
            color="bg-gradient-to-br from-[#8B1E3F] to-[#6D1830]"
          />
          <TopPerformerCard 
            title="Purple Cap" 
            data={leaderboards?.most_wickets} 
            statKey="wickets" 
            statLabel="WICKETS"
            icon={Target}
            color="bg-gradient-to-br from-purple-500 to-purple-700"
          />
          <TopPerformerCard 
            title="Maximum Sixes" 
            data={leaderboards?.most_sixes} 
            statKey="sixes" 
            statLabel="SIXES"
            icon={Zap}
            color="bg-gradient-to-br from-green-500 to-emerald-600"
          />
          <TopPerformerCard 
            title="Best Strike Rate" 
            data={leaderboards?.best_strike_rate} 
            statKey="strike_rate" 
            statLabel="STRIKE RATE"
            icon={Flame}
            color="bg-gradient-to-br from-red-500 to-rose-600"
          />
        </div>

        {/* Tabs for detailed leaderboards */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md mx-auto bg-white rounded-xl p-1 shadow">
            <TabsTrigger 
              value="batting" 
              className="rounded-lg data-[state=active]:bg-[#8B1E3F] data-[state=active]:text-white"
              data-testid="tab-batting"
            >
              Batting
            </TabsTrigger>
            <TabsTrigger 
              value="bowling"
              className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white"
              data-testid="tab-bowling"
            >
              Bowling
            </TabsTrigger>
            <TabsTrigger 
              value="fielding"
              className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="tab-fielding"
            >
              Fielding
            </TabsTrigger>
          </TabsList>

          {/* Batting Tab */}
          <TabsContent value="batting" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeaderboardCard 
                title="Most Runs" 
                icon={Trophy} 
                color="bg-gradient-to-r from-[#8B1E3F] to-[#6D1830]"
                data={leaderboards?.most_runs}
                type="batting"
                badge="Top Scorer"
              />
              <LeaderboardCard 
                title="Best Strike Rate" 
                icon={Flame} 
                color="bg-gradient-to-r from-red-500 to-rose-500"
                data={leaderboards?.best_strike_rate}
                type="strike_rate"
                badge="Min 20 balls"
              />
              <LeaderboardCard 
                title="Most Sixes" 
                icon={Zap} 
                color="bg-gradient-to-r from-green-500 to-emerald-500"
                data={leaderboards?.most_sixes}
                type="sixes"
              />
              <LeaderboardCard 
                title="Most Fours" 
                icon={TrendingUp} 
                color="bg-gradient-to-r from-blue-500 to-cyan-500"
                data={leaderboards?.most_fours}
                type="fours"
              />
              <LeaderboardCard 
                title="Best Batting Average" 
                icon={Award} 
                color="bg-gradient-to-r from-indigo-500 to-violet-500"
                data={leaderboards?.best_batting_avg}
                type="batting"
                badge="Min 2 innings"
              />
              <LeaderboardCard 
                title="Most 50s & 100s" 
                icon={Star} 
                color="bg-gradient-to-r from-yellow-500 to-amber-500"
                data={leaderboards?.most_fifties}
                type="fifties"
              />
            </div>
          </TabsContent>

          {/* Bowling Tab */}
          <TabsContent value="bowling" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeaderboardCard 
                title="Most Wickets" 
                icon={Target} 
                color="bg-gradient-to-r from-purple-500 to-violet-600"
                data={leaderboards?.most_wickets}
                type="bowling"
                badge="Purple Cap"
              />
              <LeaderboardCard 
                title="Best Economy" 
                icon={Medal} 
                color="bg-gradient-to-r from-teal-500 to-cyan-500"
                data={leaderboards?.best_economy}
                type="economy"
                badge="Min 2 overs"
              />
              <LeaderboardCard 
                title="Best Bowling Average" 
                icon={Award} 
                color="bg-gradient-to-r from-pink-500 to-rose-500"
                data={leaderboards?.best_bowling_avg}
                type="bowling"
                badge="Min 3 wickets"
              />
            </div>
          </TabsContent>

          {/* Fielding Tab */}
          <TabsContent value="fielding" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeaderboardCard 
                title="Best Fielders" 
                icon={Star} 
                color="bg-gradient-to-r from-blue-500 to-indigo-600"
                data={leaderboards?.most_catches}
                type="catches"
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
